import { useCallback, useEffect } from 'react';

import { logError } from '@verantos-dev/core-utils';
import { ConceptGroupSelection } from '@verantos-dev/types-concepts';

import { useResearchAssistantSocketContext } from '../../contexts/ResearchAssistantSocketContext';

/**
 * Socket listener for concept_group_selections event.
 *
 * The backend emits an array of JSON strings, each representing a ConceptGroupSelection.
 * Callers provide an `onConceptGroupSelections` callback to handle the parsed selections.
 */
export const useConceptGroupSelectionListeners = (
  onConceptGroupSelections?: (selections: ConceptGroupSelection[]) => void
) => {
  const { socket: currentSocket } = useResearchAssistantSocketContext();

  const conceptGroupSelectionsHandler = useCallback(
    (data: string | string[]) => {
      if (!data) return;

      try {
        const selections: ConceptGroupSelection[] = Array.isArray(data)
          ? data.map((item) => JSON.parse(item))
          : [JSON.parse(data)];

        onConceptGroupSelections?.(selections);
      } catch (error) {
        logError({
          message: 'Failed to parse concept_group_selections data',
          error: String(error),
        });
      }
    },
    [onConceptGroupSelections]
  );

  useEffect(() => {
    if (currentSocket) {
      currentSocket.on('concept_group_selections', conceptGroupSelectionsHandler);
    }
    return () => {
      if (currentSocket) {
        currentSocket.off('concept_group_selections', conceptGroupSelectionsHandler);
      }
    };
  }, [conceptGroupSelectionsHandler, currentSocket]);
};
